/**
 * Shared metric formatting helper.
 * Used in wizard, report preview, and maps to PDF formatting.
 *
 * Supported types: PERCENTAGE (default). Structured for future CURRENCY, NUMBER, RATIO.
 */

const METRIC_TYPES = {
  PERCENTAGE: { label: 'Percentage', suffix: '%', columnHeader: (label) => `${label} %` },
  CURRENCY:   { label: 'Currency',   prefix: '£', columnHeader: (label) => `${label} £` },
  NUMBER:     { label: 'Number',     suffix: '',  columnHeader: (label) => label },
  RATIO:      { label: 'Ratio',      suffix: '',  columnHeader: (label) => label },
};

/**
 * Format a metric value for display. Always 1 decimal place.
 * @param {number|null} value
 * @param {string} metricType - 'PERCENTAGE', 'CURRENCY', 'NUMBER', 'RATIO'
 * @returns {string} formatted value or '—'
 */
export function formatMetricValue(value, metricType = 'PERCENTAGE') {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  const formatted = num.toFixed(1);
  const type = METRIC_TYPES[metricType] || METRIC_TYPES.PERCENTAGE;
  if (type.prefix) return `${type.prefix}${formatted}`;
  if (type.suffix) return `${formatted}${type.suffix}`;
  return formatted;
}

/**
 * Format variance with sign and 1dp.
 */
export function formatVariance(thisVal, lastVal, metricType = 'PERCENTAGE') {
  if (thisVal == null || lastVal == null) return null;
  const variance = thisVal - lastVal;
  const type = METRIC_TYPES[metricType] || METRIC_TYPES.PERCENTAGE;
  const formatted = variance.toFixed(1);
  const sign = variance > 0 ? '+' : '';
  if (type.suffix) return `${sign}${formatted}${type.suffix}`;
  if (type.prefix) return `${sign}${type.prefix}${formatted}`;
  return `${sign}${formatted}`;
}

/**
 * Get column header suffix for a metric type.
 */
export function getMetricColumnSuffix(metricType = 'PERCENTAGE') {
  const type = METRIC_TYPES[metricType] || METRIC_TYPES.PERCENTAGE;
  return type.suffix || type.prefix || '';
}

/**
 * Get the list of supported metric types for settings dropdowns.
 */
export function getMetricTypeOptions() {
  return Object.entries(METRIC_TYPES).map(([value, config]) => ({
    value,
    label: config.label,
  }));
}
